import { memo } from "react";

import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Box from "@mui/system/Box";

import { Finding } from "stores/FindingStore";

import { GenericObject } from "utility/misc";

import FindingDetails from "components/FindingDetails";

// -----------------------------------------------------------------------------

const BoxOuterSX = {
  position: "relative",
  flex: 1,
  "@media screen and (max-width: 640px)": {
    backgroundColor: (theme: GenericObject) => theme.palette.backgroundColor,
    borderRadius: 0,
    height: "100%",
    overflowY: "auto",
    width: "100%",

    position: "fixed",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
};

const BoxInnerSX = {
  padding: "min(max(8px, calc(0.5rem + ((1vw - 8px) * 2.5))), 24px)",
  minHeight: "0vw",

  position: "absolute",
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  overflowY: "auto",

  "@media screen and (max-width: 640px)": {
    padding: 1,
  },
};

const LoadingSX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
};

const AlertSX = { maxWidth: 512, margin: "auto", mt: 2 };

// -----------------------------------------------------------------------------

const OrganizationFindingsCompare = memo(function OrganizationFindingsCompare({
  mobileMode,
  errorMessage,
  isLoading,
  findings = [],
  onFindingRemove,
}: OrganizationFindingsCompareProps): JSX.Element | null {
  if (isLoading) {
    return (
      <Box sx={LoadingSX}>
        <CircularProgress />
      </Box>
    );
  }

  //
  return !mobileMode || (mobileMode && findings.length > 0) ? (
    <Box sx={BoxOuterSX}>
      <Box sx={BoxInnerSX}>
        {errorMessage ? (
          <Alert color="error" sx={AlertSX}>
            {errorMessage}
          </Alert>
        ) : null}
        {findings.length > 0 ? (
          <Stack
            direction="column"
            spacing={3}
            alignItems="flex-start"
            width="100%"
          >
            {findings.map((finding) => {
              return (
                <FindingDetails
                  key={finding.uuid}
                  finding={finding}
                  onClose={
                    findings.length > 1 || mobileMode
                      ? () => onFindingRemove(finding.uuid)
                      : undefined
                  }
                />
              );
            })}
          </Stack>
        ) : null}
      </Box>
    </Box>
  ) : null;
});

export default OrganizationFindingsCompare;

// -----------------------------------------------------------------------------

interface OrganizationFindingsCompareProps {
  mobileMode: boolean;
  errorMessage?: string;
  isLoading: boolean;
  findings: Finding[];
  onFindingRemove: (uuid: string) => void;
}
